import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getTokens, ThemeMode, Tokens } from '../tokens';
import { applyCssVarsToRoot } from './cssVars';

type ThemeContextValue = {
  mode: ThemeMode;
  tokens: Tokens;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
};

// v2: dark became the default to match the tribed.io brand (login + landing).
// The key bump re-defaults everyone once, since v1 persisted "light" on every
// visit even for users who never touched a toggle.
const STORAGE_KEY = 'noble.theme-mode.v2';
const DEFAULT_MODE: ThemeMode = 'dark';

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return DEFAULT_MODE;
}

type Props = {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
  forcedMode?: ThemeMode;
};

export const DesignSystemThemeProvider: React.FC<Props> = ({
  children,
  defaultMode,
  forcedMode,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(
    () => forcedMode ?? defaultMode ?? readInitialMode()
  );

  useEffect(() => {
    if (forcedMode) {
      setModeState(forcedMode);
    }
  }, [forcedMode]);

  const tokens = useMemo(() => getTokens(mode), [mode]);

  useEffect(() => {
    applyCssVarsToRoot(tokens);
  }, [tokens]);

  useEffect(() => {
    if (forcedMode) return;
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode, forcedMode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, tokens, setMode, toggleMode }),
    [mode, tokens, setMode, toggleMode]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error(
      'useThemeContext must be used inside DesignSystemThemeProvider'
    );
  }
  return ctx;
}
